import { useCallback, useEffect, useMemo, useRef } from 'react'

import { BillingAddonReferenceFragment, JoinOneWaitlistDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type DatoModuleUpsellProps = {
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
}

const useJoinOneWaitlist = ({ billingAddon, moduleName }: DatoModuleUpsellProps) => {
  const [joinOneWaitlist] = useMutation(JoinOneWaitlistDocument, {
    extractorFn: (data) => data.upsellMutations.createUpsellJoinOneWaitlist,
  })
  const { currentSeats, period, configPlan, allAddons, setConfigPlan, togglePlanSelect } =
    useSelfUpsellContext()

  const addon = useMemo(() => {
    return (
      allAddons.f25Addons.find((a) => a.internalTitle === billingAddon?.internalTitle) ||
      allAddons.f25Addons[0]
    )
  }, [allAddons.f25Addons, billingAddon?.internalTitle])

  const initializedRef = useRef(false)

  useEffect(() => {
    if (addon && !initializedRef.current) {
      initializedRef.current = true
      if (!addon.isSelected) togglePlanSelect(addon)
      setConfigPlan({
        addon: [addon.pricing.getPlanId(period) || ''],
        variant: addon.activeVariantType,
      })
    }
  }, [addon])

  const joinWaitlist = useCallback(async () => {
    return await joinOneWaitlist({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem || '',
              seats: currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon || '',
                seats: currentSeats,
                module: moduleName,
              },
            ],
        origin: 'Self upsell - ONE waitlist',
      },
    })
  }, [configPlan, currentSeats, moduleName, joinOneWaitlist])

  return joinWaitlist
}

export default useJoinOneWaitlist
